/**
 * VibeMate Avatar Utilities
 *
 * Generates DiceBear avatar URLs curated for Gen Y / Gen Z / Gen Alpha.
 * Styles are mapped to Vibe Types so personality matches aesthetic.
 * Seed = display name → deterministic, same avatar every time.
 */

// Vibe type → DiceBear style mapping (each style fits the personality)
const VIBE_STYLE_MAP = {
  social_anchor:      'big-smile',   // Warm, sociable, always smiling — Gen Z favourite
  quiet_professional: 'notionists',  // Clean, minimal, intellectual — Gen Y/Z
  night_owl:          'lorelei',     // Artistic, moody illustration — Gen Y/Z
  early_riser:        'adventurer',  // Outdoorsy, active, energetic — Gen Y/Z
  free_spirit:        'open-peeps',  // Expressive, casual, earthy — Gen Z
  homebody:           'personas',    // Warm, approachable portrait — Gen Y
};

/** All selectable styles for the avatar picker, labelled by generation appeal */
export const AVATAR_STYLE_CATALOG = [
  { id: 'big-smile',    label: 'Friendly',    tag: 'Gen Z'       },
  { id: 'lorelei',      label: 'Illustrated', tag: 'Gen Y/Z'     },
  { id: 'notionists',   label: 'Minimal',     tag: 'Gen Y'       },
  { id: 'adventurer',   label: 'Adventurer',  tag: 'Gen Y/Z'     },
  { id: 'open-peeps',   label: 'Casual',      tag: 'Gen Z'       },
  { id: 'personas',     label: 'Realistic',   tag: 'Gen Y'       },
  { id: 'pixel-art',    label: 'Pixel Art',   tag: 'Gen Z/Alpha' },
  { id: 'fun-emoji',    label: 'Emoji',       tag: 'Gen Alpha'   },
  { id: 'micah',        label: 'Modern',      tag: 'Gen Z'       },
  { id: 'thumbs',       label: 'Thumbs',      tag: 'Gen Z'       },
  { id: 'bottts',       label: 'Bot',         tag: 'Gen Alpha'   },
  { id: 'avataaars',    label: 'Classic',     tag: 'Gen Y/Z'     },
];

/**
 * Returns a DiceBear avatar URL.
 * @param {string} displayName  – User's display name (used as seed)
 * @param {string} vibeType     – e.g. 'night_owl'
 * @param {string} [customStyle]– Override the vibe-mapped style
 * @param {number} [size]       – Pixel dimensions (default 200)
 */
export function getVibeAvatar(displayName, vibeType, customStyle, size = 200) {
  const seed = encodeURIComponent(
    (displayName || 'vibemate').toLowerCase().replace(/\s+/g, '-')
  );
  const style = customStyle || VIBE_STYLE_MAP[vibeType] || 'lorelei';
  return `https://api.dicebear.com/9.x/${style}/svg?seed=${seed}&size=${size}`;
}

/** Returns the user's saved avatar style from localStorage, or null */
export function getSavedAvatarStyle(userId) {
  try { return localStorage.getItem(`vm_avatar_style_${userId}`) || null; } catch { return null; }
}

/** Persists the user's chosen avatar style to localStorage */
export function saveAvatarStyle(userId, style) {
  try { localStorage.setItem(`vm_avatar_style_${userId}`, style); } catch {}
}
